import { NextRouter, useRouter } from 'next/router';
import { useEffect, useState, Dispatch, SetStateAction, useMemo, useRef } from 'react';

type Primitive = string | number | boolean;

const safeStringify = (value: unknown): string => {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return '';
};

// ---------------------------------------------------------------------------
// Batched URL writer
// Multiple useUrlState instances on the same page each compute their own
// query-key changes independently.  Without batching, each calls
// router.replace() with a snapshot of router.query that may be stale
// (the previous hook's replace hasn't settled yet), causing the last
// writer to silently drop the earlier writer's keys.
//
// The batcher accumulates every key-level mutation in the current
// micro-task and flushes them all in a single router.replace().
// ---------------------------------------------------------------------------
let pendingUpdates: Record<string, string | string[] | null> = {};
let flushScheduled = false;
let activeRouter: NextRouter | null = null;

function enqueueBatchedUpdate(router: NextRouter, updates: Record<string, string | string[] | null>): void {
  activeRouter = router;
  Object.assign(pendingUpdates, updates);

  if (!flushScheduled) {
    flushScheduled = true;
    // queueMicrotask fires after all synchronous useEffect callbacks in the
    // same React commit have executed, so every hook's writes are captured.
    queueMicrotask(() => {
      flushScheduled = false;
      const r = activeRouter;
      if (!r) return;

      const nextQuery: Record<string, string | string[]> = {};
      // Start from current router query, excluding null entries
      for (const [k, v] of Object.entries(r.query)) {
        if (v != null) {
          nextQuery[k] = v;
        }
      }

      // Apply accumulated updates
      for (const [key, value] of Object.entries(pendingUpdates)) {
        if (value === null) {
          delete nextQuery[key];
        } else {
          nextQuery[key] = value;
        }
      }

      pendingUpdates = {};
      activeRouter = null;

      if (JSON.stringify(r.query) !== JSON.stringify(nextQuery)) {
        void r.replace({ pathname: r.pathname, query: nextQuery }, undefined, { shallow: true });
      }
    });
  }
}

export const useUrlState = <T extends Record<string, Primitive | Primitive[]>>(
  defaultStateInput: T,
): [T, Dispatch<SetStateAction<T>>] => {
  const router = useRouter();
  const defaultState = useMemo(() => ({ ...defaultStateInput }), [defaultStateInput]);
  const [state, setState] = useState<T>(defaultState);
  const isHydrated = useRef(false);
  const previousState = useRef<T>(state);

  // Hydrate from URL
  useEffect(() => {
    if (!router.isReady || isHydrated.current) {
      return;
    }

    const restored = { ...defaultState };

    for (const key in defaultState) {
      if (!Object.prototype.hasOwnProperty.call(defaultState, key)) {
        continue;
      }

      const defaultValue = defaultState[key];
      const raw = router.query[key];

      if (Array.isArray(defaultValue)) {
        if (raw === undefined || raw === null) {
          restored[key] = defaultValue;
        } else if (Array.isArray(raw)) {
          restored[key] = raw.map(safeStringify) as T[typeof key];
        } else {
          restored[key] = [safeStringify(raw)] as T[typeof key];
        }
      } else if (typeof defaultValue === 'number') {
        restored[key] = raw !== undefined ? (Number(raw) as T[typeof key]) : defaultValue;
      } else if (typeof defaultValue === 'boolean') {
        restored[key] = (raw === 'true') as T[typeof key];
      } else {
        restored[key] = (raw ?? defaultValue) as T[typeof key];
      }
    }

    setState(restored);
    isHydrated.current = true;
  }, [router.isReady, router.query, defaultState]);

  // Sync state to URL (batched)
  useEffect(() => {
    if (!isHydrated.current) {
      return;
    }

    const stateChanged = JSON.stringify(previousState.current) !== JSON.stringify(state);
    if (!stateChanged) {
      return;
    }

    previousState.current = state;

    const updates: Record<string, string | string[] | null> = {};

    Object.entries(state).forEach(([key, value]) => {
      const defaultValue = defaultState[key as keyof T];

      const isEmpty =
        value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

      const isSameAsDefault = JSON.stringify(value) === JSON.stringify(defaultValue);

      if (isEmpty || isSameAsDefault) {
        updates[key] = null; // mark for removal
        return;
      }

      if (Array.isArray(value)) {
        updates[key] = value.map(safeStringify);
      } else {
        updates[key] = safeStringify(value);
      }
    });

    enqueueBatchedUpdate(router, updates);
  }, [state, defaultState, router]);

  return [state, setState];
};
